/**
 * PATH: packages/ui/src/kanban/components/lane/touchDragRegistry.ts
 * FILE_ID: UI-KANBAN-LANE-TOUCHREG-001
 * PURPOSE: Module-level registry of mounted lanes so a touch drag (which hit-
 *          tests the DOM via elementFromPoint) can route its drop into the
 *          SAME `onCardDrop` callback + card list the lane's HTML5 drop uses.
 * WHY: MOBILE-KANBAN touch drag. A touchend lands on a `[data-lane-id]`
 *      element, but the move/undo/optimistic pipeline lives on the lane
 *      component's props. This bridges DOM lane id → live lane props without
 *      duplicating any move logic (mirrors laneVirtualizeRegistry's pattern).
 * FLOW: laneId ─▶ entry { getCards, drop } ─▶ host onCardDrop (same as HTML5)
 * RELATED:
 * - useTouchDragCard (UI-KANBAN-LANE-TOUCHDRAG-001): the consumer.
 * - useKanbanLane: registers/unregisters each mounted lane.
 */
import type { Card } from '../../types'

export interface TouchDropLaneEntry {
  /** The lane's cards in the props order (positions feed the reorder math). */
  getCards: () => Card[]
  /** Fires the lane's own `onCardDrop` (host move mutation — undo/optimistic
   *  path identical to an HTML5 drop). No-op when the host didn't wire it. */
  drop: (cardId: string, position?: number, placementId?: string) => void
}

const _registry = new Map<string, TouchDropLaneEntry>()

export function registerTouchDropLane(
  laneId: string,
  entry: TouchDropLaneEntry,
): void {
  _registry.set(laneId, entry)
}

export function unregisterTouchDropLane(laneId: string): void {
  _registry.delete(laneId)
}

export function getTouchDropLane(
  laneId: string,
): TouchDropLaneEntry | undefined {
  return _registry.get(laneId)
}
